import { useParams } from 'react-router-dom';
import { useGetProjectByIdQuery } from 'src/services';
import { FormProvider, useForm } from 'react-hook-form';
import { formResolver } from 'src/utils/formResolver';
import { object } from 'yup';
import { numberScheme, stringScheme } from 'src/utils/validation-schemes';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'src/components/ui/accordion';
import { clsx } from 'clsx';
import React from 'react';
import { EditFreshchatSetupSettings } from 'src/components/Integrations/EditIntegrationPopup/EditIntegrationSettings/EditFreshchatIntegrationSettings/EditFreshchatSetupSettings/EditFreshchatSetupSettings';
import { EditAutocloseAdvancedSettings } from 'src/components/Integrations/EditIntegrationPopup/EditIntegrationSettings/EditAutocloseAdvancedSettings/EditAutocloseAdvancedSettings';
import { EditFreshchatApiDetailsSettings } from 'src/components/Integrations/EditIntegrationPopup/EditIntegrationSettings/EditFreshchatIntegrationSettings/EditFreshchatApiDetailsSettings/EditFreshchatApiDetailsSettings';
import { EditSettingsAccordionIcon } from 'src/components/Integrations/EditIntegrationPopup/EditIntegrationSettings/EditSettingsAccordionIcon';
import type { SelectedIntegration } from 'src/store/slices/integrationSlice';
import type { IntegrationSubmitHandler } from 'src/components/Integrations/integrationFormTypes';
import { useProjectAutoclose } from 'src/components/Integrations/EditIntegrationPopup/EditIntegrationSettings/useProjectAutoclose';
import type { AutocloseFormValues } from 'src/components/Integrations/EditIntegrationPopup/EditIntegrationSettings/useProjectAutoclose';
import { useLoaderWhile } from 'src/hooks/useLoaderWhile';

interface FreshchatSettingsFormValues extends AutocloseFormValues {
  chat_url: string;
  api_key: string;
  webhook_api_token: string;
}

export const EditFreshchatIntegrationSettings = ({
  disabled = false,
  integration,
  onSubmit,
}: {
  disabled?: boolean;
  integration?: SelectedIntegration;
  onSubmit?: IntegrationSubmitHandler;
}) => {
  const [accordionValue, setAccordionValue] = React.useState<string[]>([]);
  const { id } = useParams();
  const { data: currentProject } = useGetProjectByIdQuery({ id: id });
  const { autocloseHours, autocloseMinutes, saveAutoclose, isLoadingUpdateProject } =
    useProjectAutoclose(currentProject);

  useLoaderWhile(isLoadingUpdateProject);

  const methods = useForm<FreshchatSettingsFormValues>({
    defaultValues: {
      chat_url: integration?.integration_data?.chat_url,
      api_key: integration?.integration_data.api_key,
      webhook_api_token: integration?.integration_data?.webhook_api_token || '',
      autocloseEnabled: Boolean(currentProject?.autoclose_time_window_minutes),
      autocloseHours: autocloseHours,
      autocloseMinutes: autocloseMinutes,
    },
    resolver: formResolver(
      object({
        chat_url: stringScheme({ required: false }).label('Chat URL'),
        api_key: stringScheme({ required: false }).label('API Key'),
        webhook_api_token: stringScheme({ required: false }).label('Webhook api token').nullable(),
        autocloseHours: numberScheme({ required: true }),
        autocloseMinutes: numberScheme({ required: true }),
      }),
    ),
  });

  const submit = async (data: FreshchatSettingsFormValues) => {
    await saveAutoclose(data);
    delete data.autocloseEnabled;
    delete data.autocloseHours;
    delete data.autocloseMinutes;

    if (!data.api_key) {
      delete data.api_key;
    }

    if (!data.webhook_api_token) {
      delete data.webhook_api_token;
    }

    onSubmit({
      id: integration.id,
      status: integration.status,
      integration_type: integration.integration_type,
      integration_data: {
        ...integration.integration_data,
        ...data,
      },
    });
  };

  return (
    <FormProvider {...methods}>
      <form
        id="edit-integration-form"
        onSubmit={methods.handleSubmit(submit)}
        className={'max-h-[60vh] overflow-auto pr-4'}
      >
        <Accordion
          value={accordionValue}
          onValueChange={setAccordionValue}
          type="multiple"
          className="w-full"
          disabled={disabled}
        >
          <AccordionItem
            value="web-widget-settings"
            className={clsx('group bg-white border-b-[#A498FF4D]')}
          >
            <AccordionTrigger hideDefaultIcon className="py-5 cursor-pointer">
              <div className="flex row items-center justify-between w-full">
                <div>
                  <p className={'text-[#7A6BE5] font-semibold text-sm'}>
                    Set up Conversation Webhook
                  </p>
                </div>
                <div>
                  <EditSettingsAccordionIcon open={accordionValue.includes('setup-settings')} />
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-5">
              <EditFreshchatSetupSettings integration={integration} />
            </AccordionContent>
          </AccordionItem>
          <AccordionItem
            value="advanced-settings"
            className={clsx('group bg-white border-b-[#A498FF4D]')}
          >
            <AccordionTrigger hideDefaultIcon className="py-5 cursor-pointer">
              <div className="flex row items-center justify-between w-full">
                <div>
                  <p className={'text-[#7A6BE5] font-semibold text-sm'}>Advanced settings</p>
                </div>
                <div>
                  <EditSettingsAccordionIcon open={accordionValue.includes('advanced-settings')} />
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-5">
              <EditAutocloseAdvancedSettings currentProject={currentProject} />
            </AccordionContent>
          </AccordionItem>
          <AccordionItem
            value="api-details"
            className={clsx('group bg-white border-b-[#A498FF4D]')}
          >
            <AccordionTrigger hideDefaultIcon className="py-5 cursor-pointer">
              <div className="flex row items-center justify-between w-full">
                <div>
                  <p className={'text-[#7A6BE5] font-semibold text-sm'}>API details</p>
                </div>
                <div>
                  <EditSettingsAccordionIcon open={accordionValue.includes('api-details')} />
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-5">
              <EditFreshchatApiDetailsSettings integration={integration} />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </form>
    </FormProvider>
  );
};
